import React from 'react';
import { ProfitLossPoint } from '../types/billing';
import { formatAmountShort } from '../utils/formatters';

interface ProfitLossPieChartProps {
  data: ProfitLossPoint[];
}

// Modern pie/donut chart
const ProfitLossPieChart: React.FC<ProfitLossPieChartProps> = ({ data }) => {
  const width = 400;
  const height = 400;
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = 120;
  const innerRadius = 60;

  // Calculate totals
  const totalBilled = data.reduce((sum, d) => sum + d.billedAmount, 0);
  const totalReceivedRaw = data.reduce((sum, d) => sum + d.receivedAmount, 0);
  // Clamp received to not exceed billed for distribution chart
  const totalReceived = Math.min(totalReceivedRaw, totalBilled);
  const totalOutstanding = Math.max(0, totalBilled - totalReceived);

  // Create segments for the pie chart
  const segments = [
    { label: 'Received', value: totalReceived, color: '#10B981' },
    { label: 'Outstanding', value: totalOutstanding, color: '#EF4444' }
  ].filter(segment => segment.value > 0)
   .map(s => ({
     ...s,
     percentage: totalBilled > 0 ? (s.value / totalBilled) * 100 : 0
   }));

  // Calculate angles for each segment proportional to value
  let currentAngle = -Math.PI / 2; // start at top

  const createArcPath = (startAngle: number, endAngle: number, innerR: number, outerR: number) => {
    const innerStartX = centerX + Math.cos(startAngle) * innerR;
    const innerStartY = centerY + Math.sin(startAngle) * innerR;
    const innerEndX = centerX + Math.cos(endAngle) * innerR;
    const innerEndY = centerY + Math.sin(endAngle) * innerR;
    const outerStartX = centerX + Math.cos(startAngle) * outerR;
    const outerStartY = centerY + Math.sin(startAngle) * outerR;
    const outerEndX = centerX + Math.cos(endAngle) * outerR;
    const outerEndY = centerY + Math.sin(endAngle) * outerR;

    const delta = endAngle - startAngle;
    const largeArcFlag = delta > Math.PI ? 1 : 0;

    // Draw outer arc from outerStart to outerEnd, then back via inner arc
    return [
      `M ${outerStartX} ${outerStartY}`,
      `A ${outerR} ${outerR} 0 ${largeArcFlag} 1 ${outerEndX} ${outerEndY}`,
      `L ${innerEndX} ${innerEndY}`,
      `A ${innerR} ${innerR} 0 ${largeArcFlag} 0 ${innerStartX} ${innerStartY}`,
      'Z'
    ].join(' ');
  };

  // Modern color scheme
  const colors = {
    background: '#FAFAFA',
    text: '#374151',
    textLight: '#6B7280',
    border: '#E5E7EB'
  };

  if (data.length === 0 || totalBilled === 0) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-lg p-6 border border-gray-200">
        <div className="mb-4">
          <h4 className="text-lg font-semibold text-gray-800 mb-2">Revenue Distribution</h4>
          <p className="text-sm text-gray-600">Payment status breakdown</p>
        </div>
        <div className="flex items-center justify-center h-80">
          <div className="text-center">
            <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <span className="text-gray-500 text-sm">No Data</span>
            </div>
            <p className="text-gray-500">No revenue data available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-lg p-6 border border-gray-200">
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-800 mb-2">Revenue Distribution</h4>
        <p className="text-sm text-gray-600">Payment status breakdown</p>
      </div>
      
      <div className="flex items-center justify-center overflow-hidden">
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          {/* Background circle */}
          <circle
            cx={centerX}
            cy={centerY}
            r={radius}
            fill="none"
            stroke={colors.border}
            strokeWidth={2}
          />
          
          {/* Inner circle for donut effect */}
          <circle
            cx={centerX}
            cy={centerY}
            r={innerRadius}
            fill={colors.background}
            stroke={colors.border}
            strokeWidth={1}
          />

          {/* Pie segments */}
          {segments.map((segment, index) => {
            const fraction = totalBilled > 0 ? segment.value / totalBilled : 0;
            const startAngle = currentAngle;
            const endAngle = currentAngle + fraction * 2 * Math.PI;
            currentAngle = endAngle;

            const arcPath = createArcPath(startAngle, endAngle, innerRadius, radius);
            const labelAngle = (startAngle + endAngle) / 2;
            const labelRadius = (innerRadius + radius) / 2;
            const labelX = centerX + Math.cos(labelAngle) * labelRadius;
            const labelY = centerY + Math.sin(labelAngle) * labelRadius;

            return (
              <g key={index}>
                <path
                  d={arcPath}
                  fill={segment.color}
                  stroke="white"
                  strokeWidth={2}
                  className="hover:opacity-80 transition-opacity duration-200"
                >
                  <title>{`${segment.label}: ${formatAmountShort(segment.value)} (${segment.percentage.toFixed(1)}%)`}</title>
                </path>
                
                {/* Percentage labels */}
                <text
                  x={labelX}
                  y={labelY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="12"
                  fill="white"
                  fontWeight="600"
                  className="pointer-events-none"
                >
                  {segment.percentage.toFixed(0)}%
                </text>
              </g>
            );
          })}

          {/* Center text */}
          <text
            x={centerX}
            y={centerY - 10}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="14"
            fill={colors.text}
            fontWeight="600"
          >
            Total Billed
          </text>
          <text
            x={centerX}
            y={centerY + 10}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="16"
            fill={colors.text}
            fontWeight="700"
          >
            {formatAmountShort(totalBilled)}
          </text>
        </svg>
      </div>

      {/* Legend */}
      <div className="mt-6 flex justify-center space-x-8">
        {segments.map((segment, index) => (
          <div key={index} className="flex items-center space-x-2">
            <div 
              className="w-4 h-4 rounded-full" 
              style={{ backgroundColor: segment.color }}
            />
            <span className="text-sm font-medium text-gray-700">{segment.label}</span>
            <span className="text-sm text-gray-600">
              {formatAmountShort(segment.value)} ({segment.percentage.toFixed(1)}%)
            </span>
          </div>
        ))}
      </div>

      {/* Summary stats */}
      <div className="mt-6 grid grid-cols-3 gap-4 text-center">
        <div className="bg-blue-50 rounded-lg p-3">
          <div className="text-2xl font-bold text-blue-600">{formatAmountShort(totalBilled)}</div>
          <div className="text-sm text-blue-800">Total Billed</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3">
          <div className="text-2xl font-bold text-green-600">{formatAmountShort(totalReceived)}</div>
          <div className="text-sm text-green-800">Total Received</div>
        </div>
        <div className="bg-red-50 rounded-lg p-3">
          <div className="text-2xl font-bold text-red-600">{formatAmountShort(Math.max(0, totalOutstanding))}</div>
          <div className="text-sm text-red-800">Outstanding</div>
        </div>
      </div>
    </div>
  );
};

export default ProfitLossPieChart;
